import { createAction, createAsyncThunk } from '@reduxjs/toolkit'

import JobService from '../../JobService'

import { DELETE_JOB, GET_JOB, ADD_JOB, EDIT_JOB, GET_JOBS, RESET_JOB } from './job.actionTypes'

import { getNewPageOnDelete } from '../../../helpers'

import { defaultErrorMessage } from '../../../constants'

export const getJobs = createAsyncThunk(GET_JOBS, async (data = {}) => {
  try {
    const response = await JobService.getJobs(data.page)

    if (response.data?.error) {
      throw new Error(response.data?.error?.detail || response.data?.error || defaultErrorMessage)
    }

    return response.data.data
  } catch (error) {
    console.log(error)
    throw error
  }
})

export const getJob = createAsyncThunk(GET_JOB, async (data) => {
  try {
    const response = await JobService.getJob(data)

    if (response.data?.error) {
      throw new Error(response.data?.error?.detail || response.data?.error || defaultErrorMessage)
    }

    return response.data.data
  } catch (error) {
    console.log(error)
    throw error
  }
})

export const addJob = createAsyncThunk(
  ADD_JOB,
  // Destructure rejectWithValue from the second argument (thunkAPI)
  async (data, { rejectWithValue }) => {
    try {
      // Assume JobService.addJob makes the API call and returns the response object
      // It should either return the response or throw an error (like Axios does)
      const response = await JobService.addJob(data)

      // --- DEFENSIVE CHECK for response structure ---
      // Ensure we received a response and it has a data property
      if (!response || !response.data) {
        console.error('Invalid response structure received from JobService.addJob:', response)
        // Use rejectWithValue for proper error handling in Redux
        return rejectWithValue('Received an invalid response from the job service.')
      }
      // --- END CHECK ---

      // Check for application-level errors returned within the response data
      // Using optional chaining (?.) for safer access
      if (response.data.error) {
        let errorMessage = defaultErrorMessage // Start with default
        const errorData = response.data.error

        // Check if the error data is an object (like DRF validation errors)
        if (typeof errorData === 'object' && errorData !== null) {
          // DRF often uses a 'detail' key for single string errors (like auth failures)
          if (errorData.detail) {
            errorMessage = String(errorData.detail)
          } else {
            // Otherwise, try to format object values (like field validation errors)
            try {
              errorMessage = Object.values(errorData)
                .flat() // Handle nested arrays of errors
                .filter((msg) => msg != null) // Remove null/undefined
                .map((msg) => msg.toString())
                .join(', ')
              // If formatting results in an empty string, use a fallback
              if (!errorMessage) errorMessage = 'Received a structured error with no clear message.'
            } catch (formatError) {
              console.error('Could not format error object:', errorData)
              errorMessage = 'Received an unreadable structured error.'
            }
          }
        } else if (errorData) {
          // If errorData is not an object but exists (e.g., a simple string)
          errorMessage = String(errorData)
        }
        // Reject the promise with the extracted or default error message
        return rejectWithValue(errorMessage)
      }

      // --- Optional: Check for expected success data ---
      // Adjust 'response.data.data' if your success payload is structured differently
      // if (!response.data.data) {
      //    console.warn('Job added, but response missing expected data structure:', response.data);
      // }
      // --- End Check ---

      // If no response.data.error, assume success
      // Return the relevant success data payload for the 'fulfilled' action
      // Adjust 'response.data.data' based on your actual API success response
      return response.data.data || response.data // Return nested data or the whole data object
    } catch (error) {
      // Handle errors thrown by JobService.addJob (e.g., network errors, 4xx/5xx status codes if Axios throws)
      console.error('Error during addJob thunk execution:', error)

      // Attempt to extract a meaningful error message from the error object
      const apiErrorData = error.response?.data // Axios typically puts response data here on error
      let message = defaultErrorMessage // Start with default

      if (apiErrorData) {
        const errorDetail = apiErrorData.detail // Check for DRF's 'detail' key
        const errorBody = apiErrorData.error || apiErrorData // Check for 'error' key or use body

        if (errorDetail) {
          message = String(errorDetail) // Prefer 'detail' if it exists
        } else if (errorBody) {
          // Format the error body if it's an object, otherwise convert to string
          message =
            typeof errorBody === 'object'
              ? JSON.stringify(errorBody) // Stringify for visibility
              : String(errorBody)
        } else {
          // Fallback if response.data exists but structure isn't recognized
          message = `Job submission failed with status: ${error.response?.status || 'Unknown'}`
        }
      } else if (error.message) {
        // Use the standard error message if no response data is available
        message = error.message
      }

      // Use rejectWithValue to pass a serializable error payload to the 'rejected' action
      return rejectWithValue(message)
    }
  }
)

export const editJob = createAsyncThunk(EDIT_JOB, async (data) => {
  try {
    const response = await JobService.editJob(data.id, data.data)

    if (response.data?.error) {
      if (typeof response.data.error === 'object') {
        const errorMessage = Object.values(response.data.error)
          .map((error) => error.toString())
          .join(', ')
        throw new Error(errorMessage)
      }

      throw new Error(response.data?.error?.detail || response.data?.error || defaultErrorMessage)
    }
  } catch (error) {
    console.log(error)
    throw error
  }
})

export const deleteJob = createAsyncThunk(DELETE_JOB, async (data, { getState, dispatch }) => {
  try {
    const response = await JobService.deleteJob(data)

    if (response.data?.error) {
      throw new Error(response.data?.error?.detail || response.data?.error || defaultErrorMessage)
    }

    const { jobsPage, jobs } = getState().job

    await dispatch(
      getJobs({
        page: getNewPageOnDelete(jobsPage, jobs.length),
      })
    )
  } catch (error) {
    console.log(error)
    throw error
  }
})

export const resetJob = createAction(RESET_JOB)
